import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import RestEndpointsEnum from "../../../models/RestEndpointsEnum";
import IOnGetCustomerParLevelsDTO from "../../../models/DTO/IOnGetCustomerParLevelsDTO";
import ISetCustomerParLevelsDTO from "../../../models/DTO/ISetCustomerParLevelsDTO";
import { buildRestUrl, buildAuthHeadersForFetch } from "../../../services/RestService";
import { useAppContextNonNull } from "@contexts/AppContext";
import { IParLevelEditorItem, IParLevelEditorLine, ParLevelsEditor } from "../../shared/ParLevelsEditor/ParLevelsEditor";
import "./SupplierVendorParLevels.scss";

/**
 * Customer (shelf) par levels page — /<customers|vendors|dealers|...>/:guid/par-levels, reached from
 * the customer list's "Actions → Edit Par Levels". A full-width sibling of the addresses screen and
 * the customer counterpart of the vehicle detail par grid. A par of 0 removes the par (the server
 * soft-deletes the row; the driver app drops it on its next delta pull).
 */
export const SupplierVendorParLevels = () => {
    const { guid } = useParams<{ guid: string }>();
    const location = useLocation();
    const { userModel, supplierModel } = useAppContextNonNull();

    const [vendor, setVendor] = useState<{ guid: string; name: string } | null>(null);
    const [vendorNotFound, setVendorNotFound] = useState(false);
    // Guid the page is currently showing; a load whose guid no longer matches is superseded.
    const currentGuidRef = useRef(guid);

    // The route element is reused across a :guid change, so clear the previous customer's
    // identity and not-found state; otherwise a 404 would keep ParLevelsEditor unmounted
    // and the new guid would never be fetched.
    useEffect(() => {
        currentGuidRef.current = guid;
        setVendor(null);
        setVendorNotFound(false);
    }, [guid]);

    const parLevelsEnabled = !!supplierModel?.has_par_levels;
    // Keep whichever alias reached this page (/customers, /vendors, /dealers, ...) for the list crumb.
    const listPath = `/${location.pathname.split("/")[1] || "vendors"}`;

    const loadFn = useCallback(async (): Promise<IParLevelEditorItem[]> => {
        if (!guid) {
            setVendorNotFound(true);
            return [];
        }

        const url = buildRestUrl({
            endpoint: RestEndpointsEnum.GetCustomerParLevelsForVendor,
            params: { vendorGuid: guid },
        });

        const response = await fetch(url, { method: "GET", headers: buildAuthHeadersForFetch({ userModel, supplierModel }) });

        // The guid changed while this request was in flight: don't let its outcome (identity,
        // not-found, rows) land on the customer now being shown. The editor discards stale rows too.
        if (currentGuidRef.current !== guid) {
            return [];
        }

        if (response.status === 404) {
            setVendorNotFound(true);
            return [];
        }

        if (!response.ok) {
            throw new Error(`GET customer par levels failed: ${response.status}`);
        }

        const data: IOnGetCustomerParLevelsDTO = await response.json();

        if (currentGuidRef.current !== guid) {
            return [];
        }

        // The response carries the customer's identity — no separate vendor fetch needed.
        setVendor(data.vendor);

        return data.items;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guid]);

    const saveFn = async (items: IParLevelEditorLine[]): Promise<IParLevelEditorItem[]> => {
        const payload: ISetCustomerParLevelsDTO = { items };

        const url = buildRestUrl({
            endpoint: RestEndpointsEnum.SetCustomerParLevelsForVendor,
            params: { vendorGuid: guid! },
        });

        const response = await fetch(url, {
            method: "PUT",
            headers: buildAuthHeadersForFetch({ userModel, supplierModel }),
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            throw new Error(`PUT customer par levels failed: ${response.status}`);
        }

        // PUT returns the full GET shape, so hydrate from it instead of re-fetching.
        const data: IOnGetCustomerParLevelsDTO = await response.json();

        return data.items;
    };

    const crumbs: IBreadcrumbs[] = [
        { title: "dashboard", url: "/dashboard" },
        { title: "vendors", url: listPath, isVendorNameOverride: true },
        ...(vendor ? [{ title: vendor.name, url: "", skipTranslate: true }] : []),
        { title: "par_levels", url: "" },
    ];

    return (
        <div className={"SupplierVendorParLevels position-relative"} id={"supplier-vendor-par-levels"}>
            <Breadcrumbs crumbs={crumbs} supplierModel={supplierModel} />

            <div className="row mt-2">
                <div className={"col-12 d-flex align-items-center"}>
                    <OwTitle title={vendor?.name || "par_levels"} disableTranslation={!!vendor?.name} leftIconName={"fad fa-building"} />
                </div>
            </div>

            {/* Par Levels Section */}
            <div className="par-levels-section mt-4">
                <div className="section-header d-flex align-items-center">
                    <i className="fad fa-layer-group me-2" />
                    <OwTitle title={"par_levels"} tag="h4" defaultMargins={false} />
                </div>

                <div className="section-body mt-3">
                    {parLevelsEnabled && guid && !vendorNotFound ? (
                        <ParLevelsEditor
                            idPrefix={"customer-par-levels"}
                            loadFn={loadFn}
                            saveFn={saveFn}
                            helpKey={"customer_par_help"}
                            zeroNoteKey={"customer_par_zero_note"}
                        />
                    ) : (
                        <OwLabel title={vendorNotFound ? "vendor_not_found" : "par_levels_not_enabled"} noBottomMargin={true} />
                    )}
                </div>
            </div>
        </div>
    );
};
